import { ArrowStatefulIcon } from '@mm/icons';
import classnames from 'classnames';
import { get } from 'lodash';
import React, { ReactNode, useRef } from 'react';
import {
  compose,
  StateHandler,
  StateHandlerMap,
  withHandlers,
  withStateHandlers,
} from 'recompose';
import {
  ButtonColor,
  PrimaryButton,
} from '../../buttons/PrimaryButton/PrimaryButton';
import { OnClickElsewhere } from '../OnClickElsewhere/OnClickElsewhere';
import './SelectorWithArrow.scss';

export const itemsFromObject = (object: any) =>
  Object.keys(object).map<Item>(k => ({ id: k, name: object[k] }));

type Item = {
  id: number | string;
  name: string;
  [key: string]: any;
};

type OuterProps = {
  items: Item[];
  selectedItem?: Item;
  defaultLabel?: string;
  onChange(id: number | string): void;
  disabled?: boolean;
  onScrollEndReached?: () => void;
  className?: string;
  selectorClassName?: string;
  activeSelectorClassName?: string;
  selectorDropdownClassName?: string;
  buttonClassName?: string;
  arrowColor?: string;
  renderItem?: (item: Item) => ReactNode;
  renderSelectedItem?: (item: Item) => ReactNode;
  buttonText?: string;
  buttonAction?: () => void;
  customArrowIcon?: ReactNode;
  buttonDisabled?: boolean;
  arrowSize?: number;
};

type StateProps = {
  isOpened: boolean;
};

type StateHandlersProps = StateHandlerMap<StateProps> & {
  setIsOpened(value: boolean): StateHandler<StateProps>;
};

type BeforeWithHandlersProps = OuterProps & StateProps & StateHandlersProps;

type HandleClickOutside = (props: BeforeWithHandlersProps) => () => void;
type OnItemSelected = (
  props: BeforeWithHandlersProps,
) => (id: number | string) => void;
type OnActiveClick = (props: BeforeWithHandlersProps) => () => void;
type OnScroll = (
  props: OuterProps,
) => (e: React.UIEvent<HTMLDivElement>) => void;

type WithHandlerProps = {
  handleClickOutside: HandleClickOutside;
  onItemSelected: OnItemSelected;
  onActiveClick: OnActiveClick;
  onScroll: OnScroll;
};

type HandlerProps = {
  handleClickOutside: ReturnType<HandleClickOutside>;
  onItemSelected: ReturnType<OnItemSelected>;
  onActiveClick: ReturnType<OnActiveClick>;
  onScroll: ReturnType<OnScroll>;
};

type InnerProps = StateProps & StateHandlersProps & WithHandlerProps;

type Props = OuterProps & InnerProps & HandlerProps;

const defaultColor = '#959da5';

const initialState = {
  isOpened: false,
};

const stateUpdaters = {
  setIsOpened: () => (isOpened: boolean) => ({ isOpened }),
};

const handleClickOutside: HandleClickOutside =
  ({ setIsOpened }) =>
  () => {
    setIsOpened(false);
  };

const onItemSelected: OnItemSelected =
  ({ setIsOpened, onChange }) =>
  id => {
    setIsOpened(false);
    onChange(id);
  };

const onActiveClick: OnActiveClick =
  ({ disabled, setIsOpened, isOpened }) =>
  () => {
    if (!disabled) setIsOpened(!isOpened);
  };

const onScroll: OnScroll =
  ({ onScrollEndReached }) =>
  e => {
    const reachedScrollEnd =
      e.currentTarget.offsetHeight + e.currentTarget.scrollTop >=
      e.currentTarget.scrollHeight;
    if (reachedScrollEnd && onScrollEndReached) {
      onScrollEndReached();
    }
  };

const enhance = compose<InnerProps, OuterProps>(
  withStateHandlers(initialState, stateUpdaters),
  withHandlers({ handleClickOutside, onItemSelected, onActiveClick, onScroll }),
);

const SelectorWithArrow = (props: Props) => {
  const {
    selectedItem,
    items,
    arrowColor,
    onItemSelected,
    handleClickOutside,
    isOpened,
    onActiveClick,
    disabled,
    onScroll,
    defaultLabel,
    className,
    selectorClassName,
    activeSelectorClassName,
    selectorDropdownClassName,
    buttonClassName,
    renderItem,
    renderSelectedItem,
    buttonText,
    buttonAction,
    customArrowIcon,
    buttonDisabled,
    arrowSize,
  } = props;

  const direction = isOpened ? 'up' : 'down';
  const selectedItemName = get(selectedItem, 'name');

  const activeRef = useRef<HTMLDivElement>(null);

  let dropdownDirection = 'downwards';
  if (isOpened && activeRef.current) {
    const coords = activeRef.current.getBoundingClientRect();
    const spaceAvailableBelow = window.innerHeight - coords.bottom;
    const estimatedHeight = Math.min(250, items.length * 42);
    if (coords.top > estimatedHeight && spaceAvailableBelow < estimatedHeight) {
      dropdownDirection = 'upwards';
    }
  }

  const selectorClass = classnames(
    'selector-with-arrow',
    className,
    selectorClassName,
    {
      [`${className}__selector`]: className,
      'selector-with-arrow--active': isOpened,
      'selector-with-arrow--disabled': disabled,
    },
  );

  const activeSelectorClass = classnames(
    className,
    activeSelectorClassName,
    'selector-with-arrow__active',
    {
      [`${className}__active-selector`]: className,
    },
  );
  const activeItemSelectorClass = classnames(
    className,
    'selector-with-arrow__active-item',
    {
      [`${className}__active-item`]: className,
      'selector-with-arrow__active-item--default':
        !selectedItem && defaultLabel,
    },
  );
  const selectorDropdownClass = classnames(
    className,
    selectorDropdownClassName,
    `selector-with-arrow__dropdown selector-with-arrow__dropdown--${dropdownDirection}`,
    {
      [`${className}__dropdown`]: className,
    },
  );

  return (
    <OnClickElsewhere
      className={selectorClass}
      handleClick={handleClickOutside}
    >
      <div className="selector-with-arrow__selector-button-container">
        <div
          ref={activeRef}
          className={activeSelectorClass}
          onClick={onActiveClick}
        >
          <span className={activeItemSelectorClass}>
            {(selectedItem && renderSelectedItem?.(selectedItem)) ||
              selectedItemName ||
              defaultLabel}
          </span>
          {customArrowIcon || (
            <ArrowStatefulIcon
              direction={direction}
              color={arrowColor || defaultColor}
              size={arrowSize}
            />
          )}
        </div>
        {buttonText && buttonAction && (
          <PrimaryButton
            className={buttonClassName}
            disabled={buttonDisabled}
            color={ButtonColor.blue}
            onClick={buttonAction}
          >
            {buttonText}
          </PrimaryButton>
        )}
      </div>
      {isOpened && (
        <div className={selectorDropdownClass} onScroll={onScroll}>
          {items.map(item => (
            <div
              key={item.id}
              className={classnames(
                className,
                'selector-with-arrow__dropdown-item',
                {
                  [`${className}__dropdown-item`]: className,
                  [`${className}__dropdown-item--selected`]:
                    className && item.id === selectedItem?.id,
                },
              )}
              onClick={() => onItemSelected(item.id)}
            >
              {renderItem?.(item) || item.name}
            </div>
          ))}
        </div>
      )}
    </OnClickElsewhere>
  );
};

export default enhance(SelectorWithArrow);
